import { action, makeObservable, observable } from 'mobx';
import { computedFn } from 'mobx-utils';

import { BaseWidgetEntityWithWellId } from 'src/entities/widget/BaseWidgetEntityWithWellId';

import type { TGroup, TabEntity } from 'src/entities/tab/TabEntity';
import type { WidgetEntity } from 'src/entities/widget/WidgetEntity';

export class GroupManager {
  @observable.shallow groups: TGroup[];
  @observable tab: TabEntity;

  constructor(tab: TabEntity, initialGroups: TGroup[]) {
    this.groups = initialGroups;
    this.tab = tab;

    makeObservable(this);
  }

  getWidgetsByGroupId = computedFn((groupId: string | null): WidgetEntity[] => {
    if (groupId === null) return [];

    return this.tab.widgets.filter((widget) => groupId && widget.groupId === groupId);
  });

  findEmptyGroup(): string | null {
    for (const group of this.groups) {
      const widgetsInGroup = this.getWidgetsByGroupId(group.id);

      if (widgetsInGroup.length === 0) {
        return group.id;
      }
    }

    return null;
  }

  @action.bound
  getNotEmptyGroupId(): string | null {
    for (const group of this.groups) {
      let widgetsInGroup = this.tab.widgets.filter((widget) => group.id && widget.groupId === group.id);

      if (widgetsInGroup.length > 0) {
        if (widgetsInGroup.some((widget) => widget instanceof BaseWidgetEntityWithWellId && widget.wellId !== null)) {
          return group.id;
        }
      }
    }

    return null;
  }

  getWellIdInGroup(groupId: string): number | null {
    for (const widget of this.tab.widgets) {
      if (widget.groupId === groupId && widget instanceof BaseWidgetEntityWithWellId && widget.wellId !== null) {
        return widget.wellId;
      }
    }

    return null;
  }

  getFirstGroup(): string | null {
    return this.groups[0]?.id ?? null;
  }
}
